// @ts-nocheck
import { CHUNK, CELL, HOLE_W, MIN_HOLE_SEPARATION, BUCKET_SZ } from "./constants";
import { mapState, mapRefs } from "./state";
import { genChunk, getHolePos } from "./chunks";
import { openViewModal } from "./grave-render";
import { focusOnTomb } from "./camera";
import { openPurchaseModal } from "../features/purchase";
import { holeIdFromCoords } from "../utils/hole-id";
import { registerHole, tooCloseToAnyHole } from "./spatial";

/** Temple building footprint — must match backend headstone-coords.ts */
const TEMPLE_CENTER_RX = 80;
const TEMPLE_CENTER_RY = 120;

function isOnTempleCenter(wx, wy) {
  return Math.abs(wx) < TEMPLE_CENTER_RX && Math.abs(wy) < TEMPLE_CENTER_RY;
}

/**
 * Delegazione eventi per tutte le buche: un solo paio di listener su #world
 * al posto di 2 listener per ogni .hole-spot (migliaia con la mappa estesa).
 * Il suppressor click post-pan (pan.ts) è in capture sul viewport e continua
 * a bloccare i click spurii prima che arrivino qui.
 */
export function initHoleDelegation(): void {
  const world = mapRefs.world;
  if (!world) return;

  world.addEventListener("click", function (e) {
    const hole = (e.target as HTMLElement).closest?.(".hole-spot");
    if (!hole) return;
    e.stopPropagation();
    handleHoleClick(hole);
  });

  world.addEventListener("touchend", function (e) {
    const hole = (e.target as HTMLElement).closest?.(".hole-spot");
    if (!hole || mapState.panMoved) return;
    e.preventDefault();
    e.stopPropagation();
    handleHoleClick(hole);
  });
}

/** Chunk ("cx,cy") di appartenenza di un holeId a griglia, null per home_*. */
function chunkKeyForHole(holeId) {
  const parts = String(holeId).split(",");
  if (parts.length !== 2) return null;
  const gx = parseInt(parts[0], 10);
  const gy = parseInt(parts[1], 10);
  if (isNaN(gx) || isNaN(gy)) return null;
  return Math.floor(gx / CHUNK) + "," + Math.floor(gy / CHUNK);
}

function createHoleElement(holeId, wx, wy) {
  const world = mapRefs.world;
  if (!world) return null;

  const ck = chunkKeyForHole(holeId);
  const manual = ck ? (mapState.manualHoles[ck] ||= []) : null;
  // Se la buca è già nota (chunk rigenerato dopo un unload), il placement è
  // già stato validato e registrato: niente check né registerHole doppi.
  const known = manual ? manual.some((m) => m.hid === holeId) : false;

  if (!known && tooCloseToAnyHole(wx, wy, MIN_HOLE_SEPARATION)) {
    const bx = Math.floor(wx / BUCKET_SZ);
    const by = Math.floor(wy / BUCKET_SZ);
    const arr = mapState.holeBuckets[bx + "," + by];
    let isOwn = false;
    if (arr) {
      for (let k = 0; k < arr.length; k++) {
        if (Math.abs(arr[k].x - wx) < 0.1 && Math.abs(arr[k].y - wy) < 0.1) {
          isOwn = true;
          break;
        }
      }
    }
    if (!isOwn) return null;
  }

  const h = document.createElement("div");
  h.className = "hole-spot";
  h.dataset.id = holeId;
  h.style.left = wx - HOLE_W / 2 + "px";
  h.style.top = wy - HOLE_W / 2 + "px";
  world.appendChild(h);
  mapState.holeEls[holeId] = h;
  if (!known) {
    registerHole(wx, wy);
    manual?.push({ hid: holeId, x: wx, y: wy });
  }
  if (ck) (mapState.chunkEls[ck] ||= []).push(h);
  return h;
}

// Funzione helper: dato un holeId, trova o crea il buco nel DOM
// Per buchi "home_xxx" sono già creati da buildHomeScene.
// Per buchi "gx,gy" bisogna generare il chunk corrispondente e poi navigare lì.
export function ensureHoleInDOM(holeId) {
  const world = mapRefs.world;
  // Se esiste già, ritorna subito
  if (mapState.holeEls[holeId]) return mapState.holeEls[holeId];

  // Se è un buco home_, dovrebbe già esistere (creato da buildHomeScene)
  if (holeId.startsWith("home_")) {
    console.log("⚠️ Buco home non trovato:", holeId);
    return null;
  }

  // Per buchi di tipo "gx,gy", calcoliamo la posizione e generiamo il chunk
  const parts = holeId.split(",");
  if (parts.length === 2) {
    const gx = parseInt(parts[0]);
    const gy = parseInt(parts[1]);
    if (!isNaN(gx) && !isNaN(gy)) {
      // Calcola in quale chunk si trova
      const chunkCx = Math.floor(gx / CHUNK);
      const chunkCy = Math.floor(gy / CHUNK);

      // Genera il chunk (e quelli adiacenti) per creare il buco nel DOM
      genChunk(chunkCx, chunkCy);

      // Adesso dovrebbe esistere
      if (mapState.holeEls[holeId]) return mapState.holeEls[holeId];

      // Se non esiste ancora, proviamo a crearlo manualmente alla posizione corretta
      const p = getHolePos(gx, gy);
      if (p && world) {
        console.log("🔧 Creo buco manualmente per:", holeId, "a", p.x, p.y);
        return createHoleElement(holeId, p.x, p.y);
      }
    }
  }

  console.log("⚠️ Impossibile creare buco per holeId:", holeId);
  return null;
}

// Resolve world position for a saved headstone (x,y from backend).
// Falls back to grid centre when procedural placement is unavailable.
export function getHeadstoneWorldPosition(x, y) {
  const holeId = holeIdFromCoords(x, y);
  const mapped = getHoleWorldPosition(holeId);
  if (mapped && !(holeId.startsWith("home_") === false && isOnTempleCenter(mapped.x, mapped.y))) {
    return { holeId, x: mapped.x, y: mapped.y };
  }
  const wx = x * CELL;
  const wy = y * CELL;
  if (isOnTempleCenter(wx, wy)) {
    const fallback = getHoleWorldPosition("home_b3");
    if (fallback) return { holeId, x: fallback.x, y: fallback.y };
  }
  return { holeId, x: wx, y: wy };
}

// Ensure a purchased headstone hole exists even when procedural rules skip it.
export function ensureHeadstoneInDOM(x, y) {
  const target = getHeadstoneWorldPosition(x, y);
  const existing = ensureHoleInDOM(target.holeId);
  if (existing) return { hole: existing, ...target };

  if (target.holeId.startsWith("home_")) {
    return { hole: mapState.holeEls[target.holeId] ?? null, ...target };
  }

  const parts = target.holeId.split(",");
  if (parts.length === 2) {
    const gx = parseInt(parts[0], 10);
    const gy = parseInt(parts[1], 10);
    if (!isNaN(gx) && !isNaN(gy)) {
      genChunk(Math.floor(gx / CHUNK), Math.floor(gy / CHUNK));
      const hole = createHoleElement(target.holeId, target.x, target.y);
      return { hole, ...target };
    }
  }

  return { hole: null, ...target };
}

// Funzione helper: dato un holeId, calcola la posizione pixel nel mondo
export function getHoleWorldPosition(holeId) {
  // Buchi home
  // Two rows of vetrina slots below the temple — must match home-scene.ts and
  // backend headstone-coords.ts HOME_WORLD.
  const homePositions = {
    home_b1: { x: -110, y: 200 },
    home_b2: { x: 0, y: 200 },
    home_b3: { x: 110, y: 200 },
    home_t1: { x: -110, y: 340 },
    home_t2: { x: 0, y: 340 },
    home_t3: { x: 110, y: 340 },
  };
  if (homePositions[holeId]) return homePositions[holeId];

  // Buchi chunk "gx,gy"
  const parts = holeId.split(",");
  if (parts.length === 2) {
    const gx = parseInt(parts[0]);
    const gy = parseInt(parts[1]);
    if (!isNaN(gx) && !isNaN(gy)) {
      const p = getHolePos(gx, gy);
      if (p) return p;
    }
  }
  return null;
}

// Handle hole click
export async function handleHoleClick(el) {
  if (el.classList.contains("bought")) {
    const holeId = el.dataset.id;
    const pos = holeId ? getHoleWorldPosition(holeId) : null;
    if (pos) focusOnTomb(pos.x, pos.y);
    openViewModal(el);
  } else {
    openPurchaseModal(el);
  }
}
